/**
 * components/Input
 * ──────────────────────────────────────────────────────────────────────────
 * The 33px text field: `0 11px` padding, 5px radius, #E4E4E4 border. Focus
 * darkens the border to ink — there is no focus ring in this design, and
 * adding one would be the only glow on the page.
 *
 * `monospace` switches the face to IBM Plex Mono with tabular figures, which
 * every amount, id and reference field uses. `numeric` additionally sets the
 * platform keyboard, so a phone opens the number pad for an amount.
 * ──────────────────────────────────────────────────────────────────────────
 */
import { useState } from 'react';
import { StyleSheet, Text, TextInput, View, type TextInputProps } from 'react-native';
import { neutral } from '../../theme/colors';
import { noFocusOutline } from '../../theme/webStyle';
import { radii } from '../../theme/radii';
import { controlHeight } from '../../theme/spacing';
import { mono, numeric as tabular, sans, size as fontSize } from '../../theme/typography';

export interface InputProps
  extends Omit<TextInputProps, 'style' | 'placeholderTextColor' | 'value' | 'onChangeText'> {
  label?: string;
  value: string;
  onChangeText: (value: string) => void;
  placeholder?: string;
  /** Renders the design's "· required" suffix on the label. */
  required?: boolean;
  /** A hint line under the label. */
  hint?: string;
  /** Mono + tabular figures: amounts, ids, references, GL codes. */
  monospace?: boolean;
  /** Numeric keyboard on native, and mono by implication. */
  numeric?: boolean;
  /** A fixed prefix inside the field — the currency code on an amount. */
  prefix?: string;
  disabled?: boolean;
  width?: number;
}

export function Input({
  label,
  value,
  onChangeText,
  placeholder,
  required = false,
  hint,
  monospace = false,
  numeric = false,
  prefix,
  disabled = false,
  width,
  ...rest
}: InputProps) {
  const [focused, setFocused] = useState(false);
  const isMono = monospace || numeric;

  return (
    <View style={[styles.field, width !== undefined && { width }]}>
      {!!label && (
        <Text style={styles.label}>
          {label}
          {required && <Text style={styles.required}> · required</Text>}
        </Text>
      )}
      {!!hint && <Text style={styles.hint}>{hint}</Text>}

      <View
        style={[
          styles.shell,
          { borderColor: focused ? neutral.ink : neutral.border },
          disabled && styles.disabled,
        ]}
      >
        {!!prefix && <Text style={styles.prefix}>{prefix}</Text>}
        <TextInput
          value={value}
          onChangeText={onChangeText}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          editable={!disabled}
          placeholder={placeholder}
          placeholderTextColor={neutral.grey400}
          keyboardType={numeric ? 'decimal-pad' : 'default'}
          // RN's web build draws its own focus outline; the border above is
          // the design's focus indicator, so the outline is suppressed.
          style={[styles.input, isMono ? styles.inputMono : styles.inputSans]}
          {...rest}
        />
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  field: { gap: 7 },
  label: { ...sans('500'), fontSize: fontSize.label, color: neutral.ink2 },
  required: { ...sans('400'), color: neutral.grey400 },
  hint: { ...sans('400'), fontSize: fontSize.meta, color: neutral.grey600 },
  shell: {
    height: controlHeight.input,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 11,
    borderWidth: 1,
    borderRadius: radii.control,
    backgroundColor: neutral.surface,
  },
  disabled: { backgroundColor: neutral.surface2, opacity: 0.7 },
  prefix: { ...mono('500'), fontSize: fontSize.body, color: neutral.grey600 },
  input: {
    flex: 1,
    height: '100%',
    color: neutral.ink,
    padding: 0,
    ...noFocusOutline,
  },
  inputSans: { ...sans('400'), fontSize: fontSize.body },
  inputMono: { ...mono('400'), fontSize: fontSize.body, ...tabular },
});

export default Input;
